/**
 * Signup, its verify link, the login and its second step, and a session's refresh, as the device runs them against the
 * relay (part 36a, tables SU, LG and SE; decisions 24, 25, 53, 55). The password goes only to the KDF worker (C3), which
 * refuses parameters below the floor, above the ceiling or below the highest seen for the address (C7) before anything
 * is sent; the login secret goes in the one request it proves. Every challenge is signed for its purpose by the device
 * key (CH5). Nothing here reads or writes a book (C1). Every effect is injected, so it runs against the relay in tests.
 */
import { newKdfRecord } from '@cockatrice/crypto';
import type { KdfParameters, KdfRecord } from '@cockatrice/crypto';
import type { KdfAnswer, KdfJob, KdfRefusal } from '../kdf/protocol.ts';
import { toBase64Url } from './bytes.ts';
import type { RelayClient, RelayError, Reply } from './client.ts';
import { proveDevice } from './device.ts';
import type { DeviceKey, DeviceProof, Purpose } from './device.ts';
import type { Email } from './email.ts';
import { readChallenge, readCheckEmail, readKdf, readLoggedIn, readLoginAnswer, readRefreshed } from './wire.ts';
import type { AccountId, LoggedIn, Refreshed, SecondFactorNeeded } from './wire.ts';

/** What the flows run on. */
export interface FlowDeps {
  readonly client: RelayClient;
  readonly subtle: SubtleCrypto;
  /** A derivation in the KDF worker (part 25a, `deriveInWorker`). */
  readonly derive: (job: KdfJob) => Promise<KdfAnswer>;
  /** This device's key, made and kept the first time it is asked for. */
  readonly deviceKey: () => Promise<DeviceKey>;
  /** Random bytes (`crypto.getRandomValues`), for a new account's salt. */
  readonly random: (bytes: Uint8Array<ArrayBuffer>) => Uint8Array;
}

/** Why a step didn't complete: the relay refused (with its word and wait), the KDF refused or failed, or it failed. */
export type Problem =
  | { readonly kind: 'refused'; readonly error: RelayError; readonly retryAfter: number | null }
  | { readonly kind: 'unsafe'; readonly reason: KdfRefusal }
  | { readonly kind: 'failed' };

/** The versions of the terms and the privacy policy a signup accepts (table AR). */
export interface Versions {
  readonly terms: number;
  readonly privacy: number;
}

/**
 * Part 38c (GP3): what a typed password gives beyond the login: its wrapping key and KDF record (the keyring's), and the
 * login secret again, for a grant spent at once (`POST /v1/proof`) while the password is at hand.
 */
export interface Typed {
  readonly wrapping: Uint8Array<ArrayBuffer>;
  readonly kdf: KdfRecord;
  readonly secret: string;
}

/** A device admitted, and the highest KDF parameters to keep for the address (LG4), with what the password gave (GP3). */
export interface Admitted {
  readonly kind: 'logged-in';
  readonly answer: LoggedIn;
  readonly highest: KdfParameters;
  readonly typed: Typed;
}

/** A refusal or failure as a {@link Problem}. */
export function problemOf(reply: Exclude<Reply<unknown>, { kind: 'ok' }>): Problem {
  return reply.kind === 'refused' ? { kind: 'refused', error: reply.error, retryAfter: reply.retryAfter } : { kind: 'failed' };
}

/** C7: the login secret of `password` under `record`, or the KDF's refusal or failure. */
export async function secretOf(
  deps: FlowDeps,
  password: string,
  record: KdfRecord,
  highest: KdfParameters | null,
): Promise<{ readonly secret: string; readonly highest: KdfParameters; readonly typed: Typed } | Problem> {
  const answer = await deps.derive({ kind: 'derive', password, record, highest });
  if (answer.kind === 'refused') return { kind: 'unsafe', reason: answer.reason };
  if (answer.kind === 'failed') return { kind: 'failed' };
  const secret = toBase64Url(answer.secret);
  const wrapping = new Uint8Array(answer.wrapping);
  // GP3: the worker's copy of the wrapping key zeroed; only the one returned is kept, for its caller to zero.
  answer.wrapping.fill(0);
  return { secret, highest: answer.highest, typed: { wrapping, kdf: record, secret } };
}

/**
 * LG3, LG11: `send` with this device's proof for `purpose` over a fresh challenge; a `bad-challenge` (the challenge
 * ended or was used in between) is tried once more with a new one.
 */
export async function withProof<T>(deps: FlowDeps, purpose: Purpose, send: (device: DeviceProof) => Promise<Reply<T>>): Promise<Reply<T>> {
  let key: DeviceKey;
  try {
    key = await deps.deviceKey();
  } catch {
    // Deep-fix r3, table FR: the device key can't be read, made or kept (storage full, refused): a failure, never a hang.
    return { kind: 'failed' };
  }
  const once = async (): Promise<Reply<T>> => {
    const challenge = await deps.client.post('/v1/challenge', {}, readChallenge);
    if (challenge.kind !== 'ok') return challenge;
    let device: DeviceProof;
    try {
      device = await proveDevice(deps.subtle, key, purpose, challenge.value);
    } catch {
      // FR: the browser refusing to sign with the key (a key it no longer accepts).
      return { kind: 'failed' };
    }
    return send(device);
  };
  const first = await once();
  return first.kind === 'refused' && first.error === 'bad-challenge' ? once() : first;
}

/** A KDF record as a request carries it: its salt base64url (SU6). */
export function kdfJson(record: KdfRecord): { readonly version: number; readonly salt: string; readonly memoryKiB: number; readonly passes: number } {
  return { version: record.version, salt: toBase64Url(record.salt), memoryKiB: record.memoryKiB, passes: record.passes };
}

/** SU1, SU4, SU5: a signup of `email`, accepting `accepted` (part 38c: a guest's when `guest`): "check your email" (the same for every address), or why not. */
export async function signUp(deps: FlowDeps, email: Email, password: string, accepted: Versions, guest = false): Promise<{ readonly kind: 'check-email' } | Problem> {
  const record = newKdfRecord((bytes) => deps.random(bytes));
  const derived = await secretOf(deps, password, record, null);
  if (!('secret' in derived)) return derived;
  derived.typed.wrapping.fill(0);
  // Part 38c, JL1: an invitee's signup is a guest's (decision 24: no trial clock).
  const body = { email, kdf: kdfJson(record), loginSecret: derived.secret, accepted, ...(guest ? { guest: true } : {}) };
  const reply = await deps.client.post('/v1/signup', body, (value) => (readCheckEmail(value) ? true : null));
  return reply.kind === 'ok' ? { kind: 'check-email' } : problemOf(reply);
}

/** Whether two KDF records are the same record (one KDF version exists, so its field needs no comparing yet). */
function sameRecord(a: KdfRecord, b: KdfRecord): boolean {
  return a.memoryKiB === b.memoryKiB && a.passes === b.passes && toBase64Url(a.salt) === toBase64Url(b.salt);
}

/**
 * SU6, SU7: completes the signup whose link holds `token` with its password, on this device (the account's founding
 * device). `email` is the address the writer says the account is; once the account is made, the relay's salt for that
 * address must be the signup's own record, or `wrong-address` (the account is made all the same: the writer logs in
 * with the right address).
 */
export async function verifyLink(deps: FlowDeps, token: string, email: Email, password: string, highest: KdfParameters | null): Promise<Admitted | { readonly kind: 'wrong-address' } | Problem> {
  const salt = await deps.client.post('/v1/signup/salt', { token }, readKdf);
  if (salt.kind !== 'ok') return problemOf(salt);
  const derived = await secretOf(deps, password, salt.value, highest);
  if (!('secret' in derived)) return derived;
  const reply = await withProof(deps, 'verify', (device) => deps.client.post('/v1/signup/verify', { token, loginSecret: derived.secret, device }, readLoggedIn));
  if (reply.kind !== 'ok') {
    derived.typed.wrapping.fill(0);
    return problemOf(reply);
  }
  const own = await deps.client.post('/v1/login/salt', { email }, readKdf);
  if (own.kind !== 'ok' || !sameRecord(own.value, salt.value)) {
    // GP3: no admission to hand the wrapping key to: zeroed.
    derived.typed.wrapping.fill(0);
    return { kind: 'wrong-address' };
  }
  return { kind: 'logged-in', answer: reply.value, highest: derived.highest, typed: derived.typed };
}

/** LG1–LG8, LG12: logs `email` in with `password`: admitted, the second step to take, or why not. */
export async function logIn(
  deps: FlowDeps,
  email: Email,
  password: string,
  highest: KdfParameters | null,
): Promise<Admitted | { readonly kind: 'second-factor'; readonly need: SecondFactorNeeded; readonly highest: KdfParameters; readonly typed: Typed } | Problem> {
  const salt = await deps.client.post('/v1/login/salt', { email }, readKdf);
  if (salt.kind !== 'ok') return problemOf(salt);
  const derived = await secretOf(deps, password, salt.value, highest);
  if (!('secret' in derived)) return derived;
  const reply = await withProof(deps, 'login', (device) => deps.client.post('/v1/login', { email, loginSecret: derived.secret, device }, readLoginAnswer));
  if (reply.kind !== 'ok') {
    derived.typed.wrapping.fill(0);
    return problemOf(reply);
  }
  return reply.value.kind === 'logged-in'
    ? { kind: 'logged-in', answer: reply.value, highest: derived.highest, typed: derived.typed }
    : { kind: 'second-factor', need: reply.value, highest: derived.highest, typed: derived.typed };
}

/** The second factor a login's second step gives (LF7′, LF9′, LF10′): a TOTP code, a backup code, or a passkey's answer. */
export type SecondProof = { readonly totp: string } | { readonly backupCode: string } | { readonly passkey: unknown };

/** LG5, LG9, LG10: the second step of a login with `ticket`: admitted (with the backup codes left), or why not. */
export async function secondStep(deps: FlowDeps, ticket: string, proof: SecondProof): Promise<{ readonly kind: 'logged-in'; readonly answer: LoggedIn } | Problem> {
  const reply = await withProof(deps, 'factor', (device) => deps.client.post('/v1/login/second-factor', { ticket, device, ...proof }, readLoggedIn));
  return reply.kind === 'ok' ? { kind: 'logged-in', answer: reply.value } : problemOf(reply);
}

/**
 * Fix-next 84, table LXC: this device logs itself out of `account` at the relay, signed by its key (no session: one may
 * have run out, LXC6). `done` once the relay took it, or for a refusal no retry can change (a body it can't read);
 * `failed` for anything a later try may get past (the network, the relay down, a limit), so it stays owed (LXC3).
 */
export async function logOutDevice(deps: FlowDeps, account: AccountId): Promise<'done' | 'failed'> {
  const reply = await withProof(deps, 'logout', (device) => deps.client.post('/v1/logout', { account, device }, () => true));
  return reply.kind === 'ok' || (reply.kind === 'refused' && reply.error === 'bad-request') ? 'done' : 'failed';
}

/** SE1–SE4: a new session for `account` from this device's key, with a new trial copy, or why not. */
export async function refreshSession(deps: FlowDeps, account: AccountId): Promise<{ readonly kind: 'refreshed'; readonly value: Refreshed } | Problem> {
  const reply = await withProof(deps, 'session', (device) => deps.client.post('/v1/session', { account, device }, readRefreshed));
  return reply.kind === 'ok' ? { kind: 'refreshed', value: reply.value } : problemOf(reply);
}
